import { useEffect, useState } from 'react';
import type {
  VenueUpgradeDetailsQuery,
  VenueCustomerPlanDefinitionsQuery,
} from '@pv/common/graphql/operations';
type Venue = VenueUpgradeDetailsQuery['venue'];
type PlanDefinition =
  VenueCustomerPlanDefinitionsQuery['venueCustomerPlanDefinitions'][number];
interface UsePlanSelectionParams {
  availablePlans: VenueCustomerPlanDefinitionsQuery['venueCustomerPlanDefinitions'];
  venue: Venue | null;
  initialTier?: string;
  initialBillingInterval?: string;
}

interface UsePlanSelectionReturn {
  currentPlan: PlanDefinition | null;
  selectedPlan: PlanDefinition | null;
  setSelectedPlan: (plan: PlanDefinition | null) => void;
}

/**
 * Hook for managing plan selection logic
 */
export const usePlanSelection = ({
  availablePlans,
  venue,
  initialTier,
  initialBillingInterval,
}: UsePlanSelectionParams): UsePlanSelectionReturn => {
  const [currentPlan, setCurrentPlan] = useState<PlanDefinition | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanDefinition | null>(null);

  // Initialize plan from initialTier and initialBillingInterval
  useEffect(() => {
    if (!availablePlans.length) {
      return;
    }
    if (initialTier && initialBillingInterval) {
      const plan = availablePlans.find(
        (p) =>
          p.tier === initialTier && p.billingInterval === initialBillingInterval
      );
      setSelectedPlan(plan || null);
      setCurrentPlan(plan || null);
    }
  }, [availablePlans, initialBillingInterval, initialTier]);

  // Initialize plan from venue's current subscription
  useEffect(() => {
    if (venue?.subscription && !initialTier && !initialBillingInterval) {
      const planToSelect = availablePlans.find(
        (p) =>
          p.tier === venue.subscription?.tier &&
          p.billingInterval === venue.subscription?.billingInterval
      );
      if (planToSelect) {
        setSelectedPlan(planToSelect);
        setCurrentPlan(planToSelect);
      } else {
        const defaultPlan = availablePlans.find(
          (p) => p.tier === 'professional' && p.billingInterval === 'monthly'
        );
        setSelectedPlan(defaultPlan || null);
      }
    }
  }, [venue, availablePlans, initialTier, initialBillingInterval]);

  return {
    currentPlan,
    selectedPlan,
    setSelectedPlan,
  };
};
